/**
 * Form escalation questions on the chat's side of the wire (ADR-20261001
 * "escalation-form-questions-amendment", PLAN-20261001 slice 20,
 * agion/Agion#3437): which forms the screen can answer, the form_values an
 * answer sends, the fields a recorded answer names, and the evidence a form
 * asks about. The runner on the Edge validates every value with the shared Go
 * validator; the browser checks only what its native inputs report, so it
 * never sends a value the person did not type.
 */

import type {
  Escalation,
  EscalationAnswer,
  EscalationQuestion,
  EscalationQuestionFormField,
} from '../types/escalations'

/** The field kinds the screen renders; any other kind leaves the form without an answer. */
const FORM_FIELD_KINDS = new Set(['text', 'number', 'date'])

/**
 * What one input holds: the text the browser reports, and whether the
 * browser could not read what was typed (validity.badInput, as a number or
 * date input reports a partial entry with an empty value).
 */
export interface FormFieldEntry {
  value: string
  badInput: boolean
}

export type FormEntries = Record<string, FormFieldEntry>

/**
 * The fields of a form the screen can answer, in order; undefined for a
 * question that is not a form, a form without fields, or one with a field
 * kind the screen does not render.
 */
export function answerableFormFields(question: EscalationQuestion): EscalationQuestionFormField[] | undefined {
  if (question.type !== 'form') return undefined
  const fields = question.fields ?? []
  if (fields.length === 0 || fields.some((field) => !FORM_FIELD_KINDS.has(field.kind))) return undefined
  return fields
}

/** Whether a field's entry is blank: nothing typed, or only spaces. */
function isBlank(entry: FormFieldEntry | undefined): boolean {
  return !entry || entry.value.trim() === ''
}

/**
 * Whether a field holds what the browser cannot send: an entry its input
 * could not read, or a number that does not parse as a finite number.
 * Bounds, lengths and dates the input accepted are the runner's to judge.
 */
export function formFieldInvalid(field: EscalationQuestionFormField, entry: FormFieldEntry | undefined): boolean {
  if (!entry) return false
  if (entry.badInput) return true
  if (field.kind !== 'number' || isBlank(entry)) return false
  return !Number.isFinite(Number(entry.value))
}

/**
 * The form_values of an answer: each filled field by id, a number as a JSON
 * number and text and dates as typed; blank fields are left out, so a form
 * nobody could fill sends {}. Undefined while any field is invalid.
 */
export function formValuesOf(
  fields: EscalationQuestionFormField[],
  entries: FormEntries,
): Record<string, string | number> | undefined {
  const values: Record<string, string | number> = {}
  for (const field of fields) {
    const entry = entries[field.id]
    if (formFieldInvalid(field, entry)) return undefined
    if (!entry || isBlank(entry)) continue
    values[field.id] = field.kind === 'number' ? Number(entry.value) : entry.value
  }
  return values
}

/**
 * The labels of the fields a recorded form answer names, in the question's
 * order; [] when it answered none, undefined when governance names no
 * fields. An id the question does not have is left out, so no id reaches the
 * screen.
 */
export function answeredFieldLabels(
  question: EscalationQuestion,
  answer: EscalationAnswer | undefined,
): string[] | undefined {
  const ids = answer?.answered_field_ids
  if (!Array.isArray(ids)) return undefined
  return (question.fields ?? []).filter((field) => ids.includes(field.id)).map((field) => field.label)
}

/** The run output a form's evidence is, as POST /v1/streaming/run-output/open takes it. */
export interface EvidenceRef {
  mission_id: string
  execution_id: string
  host_id: string
  artifact_id: string
}

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value !== ''
}

/**
 * The evidence an escalation names in its metadata, with the run that holds
 * it (the escalation's, else the question asker's, else the run the
 * runner names in metadata.execution_id); undefined when the metadata names
 * no evidence or the run is not known.
 */
export function evidenceRefOf(escalation: Escalation): EvidenceRef | undefined {
  const hostId = escalation.metadata?.evidence_host_id
  const artifactId = escalation.metadata?.evidence_artifact_id
  const missionId = escalation.mission_id || escalation.question?.asker?.mission_id
  const executionId =
    escalation.execution_id || escalation.question?.asker?.execution_id || escalation.metadata?.['execution_id']
  if (!nonEmpty(hostId) || !nonEmpty(artifactId) || !nonEmpty(missionId) || !nonEmpty(executionId)) return undefined
  return { mission_id: missionId, execution_id: executionId, host_id: hostId, artifact_id: artifactId }
}

/** Evidence as the screen shows it: a JSON object or array pretty-printed, anything else as it came. */
export function evidenceText(content: string): string {
  try {
    const parsed: unknown = JSON.parse(content)
    if (parsed !== null && typeof parsed === 'object') return JSON.stringify(parsed, null, 2)
  } catch {
    // Not JSON: shown as it came.
  }
  return content
}

/** One part of an evidence object whose values are all text: its key and its text as written. */
export interface EvidenceSection {
  key: string
  text: string
}

/**
 * Evidence as the screen lays it out. An object whose values are all text,
 * such as an extract specialist's {task, context}, is one labelled section
 * per key with its text and real line breaks; anything else is evidenceText.
 */
export type EvidenceView = { kind: 'sections'; sections: EvidenceSection[] } | { kind: 'text'; text: string }

export function evidenceView(content: string): EvidenceView {
  let parsed: unknown
  try {
    parsed = JSON.parse(content)
  } catch {
    return { kind: 'text', text: content }
  }
  if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const entries = Object.entries(parsed)
    if (entries.length > 0 && entries.every(([, value]) => typeof value === 'string')) {
      return { kind: 'sections', sections: entries.map(([key, value]) => ({ key, text: value as string })) }
    }
  }
  return { kind: 'text', text: evidenceText(content) }
}

/** The label of an evidence section: Task, Context and Constraints translated, any other key as it is. */
export function evidenceSectionLabel(key: string, t: (key: string, fallback: string) => string): string {
  switch (key) {
    case 'task':
      return t('escalations.evidence.task', 'Task')
    case 'context':
      return t('escalations.evidence.context', 'Context')
    case 'constraints':
      return t('escalations.evidence.constraints', 'Constraints')
    default:
      return key
  }
}
